import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Chip
} from '@mui/material';
import { Person, Schedule } from '@mui/icons-material';
import Sidebar from '../components/Sidebar.tsx';
import { useAuth } from '../context/AuthContext.tsx';

const API_BASE = process.env.REACT_APP_API_URL + '/api';

interface Assignment {
  date: string;
  type: string;
  tour_name: string;
}

interface DayEntry {
  day: string;
  vroege: string;
  late: string;
}



  const generateMonths = (before = 2, after = 2) => {
  const months = [];
  const now = new Date();

  for (let i = -before; i <= after; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const label = d.toLocaleString("nl-BE", { month: "short", year: "numeric" });

    months.push({ value, label });
  }

  return months;
};

const isWeekend = (year: number, month: number, day: number) => {
  const date = new Date(year, month - 1, day);
  const dayOfWeek = date.getDay(); // 0 = zondag, 6 = zaterdag
  return dayOfWeek === 0 || dayOfWeek === 6;
};


const months = generateMonths(2, 2);


const PersoonlijkOverzicht: React.FC = () => {
  const { user } = useAuth();
  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2,"0")}`);
  const [year, month] = String(selectedMonth).split('-').map(Number);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleSidebarToggle = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleMonthChange = (event: any) => {
    setSelectedMonth(event.target.value);
  };

  const [personalSchedule, setPersonalSchedule] = useState<DayEntry[]>([]);
  const [workingHours, setWorkingHours] = useState({
    totalHours: 0,
    earlies: 0,
    lates: 0,
  });

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;

    const fetchData = async () => {
      try {
        const token = localStorage.getItem("auth_token");

        const res = await fetch(`${API_BASE}/assignments`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (!res.ok) throw new Error("Assignments fetch failed");

        const assignments: Assignment[] = await res.json();

        const daysInSelectedMonth = new Date(year, month, 0).getDate();

        const days: DayEntry[] = Array.from(
          { length: daysInSelectedMonth },
          (_, i) => ({
            day: String(i + 1).padStart(2, "0"),
            vroege: "-",
            late: "-"
          })
        );

        let earlyCount = 0;
        let lateCount = 0;


          assignments
            .filter(a => {
              const d = new Date(a.date);
              return d.getFullYear() === year && (d.getMonth() + 1) === month;
            })
            .forEach(a => {
            const dayIndex = Number(a.date.split("-")[2]) - 1;
            if (!days[dayIndex]) return;

            if (a.type === "vroege") {
              if (days[dayIndex].vroege === "-") {
                days[dayIndex].vroege = a.tour_name;
                earlyCount++;
              }
            }

            if (a.type === "late") {
              if (days[dayIndex].late === "-") {
                days[dayIndex].late = a.tour_name;
                lateCount++;
              }
            }
          });

        setPersonalSchedule(days);
        setWorkingHours({
          totalHours: Number(((earlyCount + lateCount) * 7.6).toFixed(1)),
          earlies: earlyCount,
          lates: lateCount
        });
      } catch (err) {
        console.error("Agenda ophalen mislukt:", err);
      }
    };

    fetchData();
  }, [user, selectedMonth]);




  return (
    <Box sx={{ display: 'flex' }}>
      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      
      <Container maxWidth="lg" sx={{ 
        mt: { xs: '60px', md: 4 }, 
        mb: 4, 
        flexGrow: 1,
        ml: { xs: 0, md: '70px' },
        p: { xs: 1, sm: 2, md: 3 }
      }}>
        <Typography 
          variant="h4" 
          component="h1" 
          gutterBottom 
          sx={{ 
            color: '#4aa8b4', 
            fontWeight: 'bold',
            fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' }
          }}
        >
          <Person sx={{ mr: { xs: 1, md: 2 }, verticalAlign: 'middle' }} />
          Persoonlijk Overzicht
        </Typography>

        {/* Gebruiker informatie */}
        <Paper sx={{ p: 3, mb: 3, backgroundColor: '#f8f9fa' }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h6" sx={{ color: '#4aa8b4', mb: 1 }}>
                <Schedule sx={{ mr: 1, verticalAlign: 'middle' }} />
                Mijn Werkrooster
              </Typography>
              <Typography variant="body1">
                <strong>Verpleegkundige:</strong> {user?.display_name || user?.name || 'Onbekend'}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
              <InputLabel>Selecteer een maand</InputLabel>
              <Select
                value={selectedMonth}
                onChange={handleMonthChange}
                label="Selecteer een maand"
              >
                {months.map(m => (
                  <MenuItem key={m.value} value={m.value}>
                    {m.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            </Grid>
          </Grid>
        </Paper>

        {/* Samenvatting uren */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ color: '#4aa8b4' }}>
            Uren Overzicht - {selectedMonth}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6} md={2}>
              <Chip 
                label={`${workingHours.totalHours.toFixed(1)} Uur`} 
                color="primary" 
                variant="outlined" 
                sx={{ width: '100%' }} 
              />
              <Typography variant="caption" display="block" textAlign="center" sx={{ mt: 1 }}>
                Totaal Uren
              </Typography>
            </Grid>
            <Grid item xs={6} md={2}>
              <Chip 
                label={`${workingHours.earlies} Vroeges`} 
                color="secondary" 
                variant="outlined" 
                sx={{ width: '100%' }} 
              />
              <Typography variant="caption" display="block" textAlign="center" sx={{ mt: 1 }}>
                Vroege Diensten
              </Typography>
            </Grid>
            <Grid item xs={6} md={2}>
              <Chip 
                label={`${workingHours.lates} Lates`} 
                color="warning" 
                variant="outlined" 
                sx={{ width: '100%' }} 
              />
              <Typography variant="caption" display="block" textAlign="center" sx={{ mt: 1 }}>
                Late Diensten
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        {/* Persoonlijke maandagenda */}
        <Paper sx={{ p: { xs: 1, sm: 2 } }}>
          <Typography 
            variant="h6" 
            gutterBottom 
            sx={{ 
              color: '#4aa8b4', 
              mb: 3,
              fontSize: { xs: '1.1rem', sm: '1.25rem' }
            }}
          >
            Mijn Agenda - {selectedMonth}
          </Typography>
          
          <Box sx={{ 
            overflowX: 'auto',
            width: '100%',
            '&::-webkit-scrollbar': {
              height: '6px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: '#f1f1f1',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: '#c1c1c1',
              borderRadius: '3px',
            }
          }}>
            <Table size="small" sx={{ minWidth: { xs: 400, sm: 'auto' } }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell 
                  align="center" 
                  sx={{ 
                    fontWeight: 'bold', 
                    minWidth: { xs: 40, sm: 60 },
                    width: { xs: 40, sm: 60 },
                    fontSize: { xs: '0.75rem', sm: '0.875rem' }
                  }}
                >
                  Dag
                </TableCell>
                <TableCell 
                  align="center" 
                  sx={{ 
                    fontWeight: 'bold', 
                    minWidth: { xs: 120, sm: 150 },
                    fontSize: { xs: '0.75rem', sm: '0.875rem' }
                  }}
                >
                  Vroege
                </TableCell>
                <TableCell 
                  align="center" 
                  sx={{ 
                    fontWeight: 'bold', 
                    minWidth: { xs: 120, sm: 150 },
                    fontSize: { xs: '0.75rem', sm: '0.875rem' }
                  }}
                >
                  Late
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {personalSchedule.map((row) => {
                const dayNumber = Number(row.day);
                const weekend = isWeekend(year, month, dayNumber);
                const cellBg = weekend ? '#e0e0e0' : '#ffffff';

                return (
                  <TableRow
                      key={row.day}
                      sx={{
                        backgroundColor: cellBg,
                        '&:hover': { backgroundColor: cellBg },
                        '&:focus, &:focus-visible, &.Mui-selected': {
                          backgroundColor: cellBg,
                          outline: 'none',
                        },
                      }}
>
                    <TableCell 
                      align="center" 
                      sx={{ 
                        fontWeight: 'bold',
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        p: { xs: '4px', sm: '8px' },
                        backgroundColor: cellBg
                      }}
                    >
                      {row.day}
                    </TableCell>
                    <TableCell 
                      align="center"
                      sx={{ 
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        p: { xs: '4px', sm: '8px' },
                        backgroundColor: cellBg
                      }}
                    >
                      {row.vroege !== '-' ? (
                        <Chip 
                          label={row.vroege} 
                          color="primary" 
                          size="small" 
                          variant="outlined"
                          sx={{ fontSize: { xs: '0.6rem', sm: '0.75rem' } }}
                        />
                      ) : (
                        row.vroege
                      )}
                    </TableCell>
                    <TableCell 
                      align="center"
                      sx={{ 
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        p: { xs: '4px', sm: '8px' },
                        backgroundColor: cellBg
                      }}
                    >
                      {row.late !== '-' ? (
                        <Chip 
                          label={row.late} 
                          color="secondary" 
                          size="small"
                          sx={{ fontSize: { xs: '0.6rem', sm: '0.75rem' } }} 
                          variant="outlined"
                        />
                      ) : (
                        row.late
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default PersoonlijkOverzicht;
